import { Routes } from '@angular/router';
import { authRoutes, canActivateAuth } from '@origin8-web/core-ui/login/routes';

import {
  APP_ROUTE_TITLE,
  INTEGRATIONS_ROUTE,
  INTERACTION_TRANSCRIPT_ROUTE,
  INTERACTIONS_HISTORY_ROUTE,
  LEAD_LIFECYCLE_ROUTE,
  LEADS_LIBRARY_ROUTE,
  PHONE_NUMBERS_ROUTE,
  RULES_EXPLORER_ROUTE,
  RULES_ROUTE,
  SUPERVISORS_ROUTE,
  SUPERVISOR_VIEW_ROUTE,
  UNAUTHORIZED_PATH,
} from '../app.interface';
import { confirmLeavingRulesExplorer } from '../features/rules-explorer/rules-explorer.guard';
import { canActivateView } from '../core/permissions/required-permissions';
import { canActivateOrganization } from '../core/guards/organization.guard';
import { redirectToOrganizationScopedPath } from '../core/utils/organization-redirect.util';

export const routes: Routes = [
  ...authRoutes,
  {
    path: INTERACTION_TRANSCRIPT_ROUTE.DETAILS,
    canActivate: [canActivateAuth, canActivateView('viewInteractionsReport'), canActivateOrganization],
    data: {
      pageTitle: 'Interaction Report Details',
    },
    loadComponent: () =>
      import('../features/interaction-transcript/interaction-transcript.component').then(
        (c) => c.InteractionTranscriptComponent,
      ),
  },
  {
    path: LEAD_LIFECYCLE_ROUTE.BASE,
    loadComponent: () =>
      import('../features/lead-lifecycle-dashboard/lead-lifecycle-dashboard.component').then(
        (mod) => mod.LeadLifecycleDashboardComponent,
      ),
    data: {
      pageTitle: 'Lead lifecycle',
    },
    canActivate: [canActivateAuth, canActivateView('viewLeadLifecycle'), canActivateOrganization],
  },
  {
    path: LEAD_LIFECYCLE_ROUTE.CONFIGURATION,
    loadComponent: () =>
      import(
        '../features/lead-lifecycle-configuration/lead-lifecycle-configurations-list/lead-lifecycle-configurations-list.component'
      ).then((mod) => mod.LeadLifecycleConfigurationsListComponent),
    data: {
      pageTitle: 'Lifecycle configurations',
    },
    canActivate: [canActivateAuth, canActivateView('viewLeadLifecycleConfigurations'), canActivateOrganization],
  },
  {
    path: LEAD_LIFECYCLE_ROUTE.CONFIGURATION_CREATE,
    loadComponent: () =>
      import('../features/lead-lifecycle-configuration/lead-lifecycle-configuration.component').then(
        (mod) => mod.LeadLifecycleConfigurationComponent,
      ),
    data: {
      pageTitle: 'Create lifecycle configuration',
    },
    canActivate: [canActivateAuth, canActivateView('viewLeadLifecycleConfigEditor'), canActivateOrganization],
  },
  {
    path: LEAD_LIFECYCLE_ROUTE.CONFIGURATION_EDIT,
    loadComponent: () =>
      import('../features/lead-lifecycle-configuration/lead-lifecycle-configuration.component').then(
        (mod) => mod.LeadLifecycleConfigurationComponent,
      ),
    data: {
      pageTitle: 'Edit lifecycle configuration',
    },
    canActivate: [canActivateAuth, canActivateView('viewLeadLifecycleConfigEditor'), canActivateOrganization],
  },
  {
    path: INTERACTIONS_HISTORY_ROUTE.BASE,
    loadComponent: () =>
      import('../features/interactions-history/interactions-history.component').then(
        (mod) => mod.InteractionsHistoryComponent,
      ),
    data: {
      pageTitle: 'Call History',
    },
    canActivate: [canActivateAuth, canActivateView('viewInteractionsHistory'), canActivateOrganization],
  },
  {
    path: LEADS_LIBRARY_ROUTE.BASE,
    loadComponent: () =>
      import('../features/leads-library/leads-library.component').then((mod) => mod.LeadsLibraryComponent),
    data: {
      pageTitle: 'Leads',
    },
    canActivate: [canActivateAuth, canActivateView('viewLeadsLibrary'), canActivateOrganization],
  },
  {
    path: RULES_ROUTE.BASE,
    loadComponent: () => import('../features/rules/rules.component').then((mod) => mod.RulesComponent),
    data: {
      pageTitle: 'Rules Library',
    },
    canActivate: [canActivateAuth, canActivateView('viewRules'), canActivateOrganization],
  },
  {
    path: RULES_ROUTE.ADD,
    loadComponent: () =>
      import('../features/rules/rule-editor/rule-editor.component').then((mod) => mod.RuleEditorComponent),
    data: {
      pageTitle: 'Add Rule',
    },
    canActivate: [canActivateAuth, canActivateView('viewRuleEditor'), canActivateOrganization],
  },
  {
    path: RULES_ROUTE.EDIT,
    loadComponent: () =>
      import('../features/rules/rule-editor/rule-editor.component').then((mod) => mod.RuleEditorComponent),
    data: {
      pageTitle: 'Edit Rule',
    },
    canActivate: [canActivateAuth, canActivateView('viewRuleEditor'), canActivateOrganization],
  },
  {
    path: RULES_EXPLORER_ROUTE.BASE,
    loadComponent: () =>
      import('../features/rules-explorer/rules-explorer.component').then((mod) => mod.RulesExplorerComponent),
    data: {
      pageTitle: APP_ROUTE_TITLE.RULES_EXPLORER,
    },
    canActivate: [canActivateAuth, canActivateView('viewRulesExplorer'), canActivateOrganization],
    // Leaving drops a run that only lives in memory; the page asks first while one is running or done.
    canDeactivate: [confirmLeavingRulesExplorer],
  },
  {
    path: SUPERVISORS_ROUTE.BASE,
    loadComponent: () =>
      import('../features/supervisors/supervisors.component').then((mod) => mod.SupervisorsComponent),
    data: {
      pageTitle: 'Supervisors',
    },
    canActivate: [canActivateAuth, canActivateView('viewSupervisors'), canActivateOrganization],
  },
  {
    path: SUPERVISORS_ROUTE.ADD,
    loadComponent: () =>
      import('../features/supervisors/supervisor-editor/supervisor-editor.component').then(
        (mod) => mod.SupervisorEditorComponent,
      ),
    data: {
      pageTitle: 'Add Supervisor',
    },
    canActivate: [canActivateAuth, canActivateView('viewSupervisorEditor'), canActivateOrganization],
  },
  {
    path: SUPERVISORS_ROUTE.EDIT,
    loadComponent: () =>
      import('../features/supervisors/supervisor-editor/supervisor-editor.component').then(
        (mod) => mod.SupervisorEditorComponent,
      ),
    data: {
      pageTitle: 'Edit Supervisor',
    },
    canActivate: [canActivateAuth, canActivateView('viewSupervisorEditor'), canActivateOrganization],
  },
  {
    path: SUPERVISOR_VIEW_ROUTE.BASE,
    loadComponent: () =>
      import('../features/supervisor-view/supervisor-view.component').then((mod) => mod.SupervisorViewComponent),
    data: {
      pageTitle: 'Supervisor',
    },
    canActivate: [canActivateAuth, canActivateView('viewSupervisorDetail'), canActivateOrganization],
    children: [
      { path: '', pathMatch: 'full', redirectTo: SUPERVISOR_VIEW_ROUTE.TRENDS },
      {
        path: SUPERVISOR_VIEW_ROUTE.TRENDS,
        loadComponent: () =>
          import('../features/supervisor-view/tabs/trends-tab.component').then((mod) => mod.TrendsTabComponent),
        data: { pageTitle: 'Supervisor — Trends' },
      },
      {
        path: SUPERVISOR_VIEW_ROUTE.ACTIVITY,
        loadComponent: () =>
          import('../features/supervisor-view/tabs/activity-tab.component').then((mod) => mod.ActivityTabComponent),
        data: { pageTitle: 'Supervisor — Activity' },
      },
      {
        path: SUPERVISOR_VIEW_ROUTE.COMPLIANCE,
        loadComponent: () =>
          import('../features/supervisor-view/tabs/compliance-tab.component').then((mod) => mod.ComplianceTabComponent),
        data: { pageTitle: 'Supervisor — Compliance' },
      },
      {
        path: SUPERVISOR_VIEW_ROUTE.RISK,
        loadComponent: () =>
          import('../features/supervisor-view/tabs/risk-tab.component').then((mod) => mod.RiskTabComponent),
        data: { pageTitle: 'Supervisor — Risk' },
      },
      {
        path: SUPERVISOR_VIEW_ROUTE.INTERACTIONS,
        loadComponent: () =>
          import('../features/supervisor-view/tabs/calls-tab.component').then((mod) => mod.CallsTabComponent),
        data: { pageTitle: 'Supervisor — Calls' },
      },
    ],
  },
  {
    path: INTEGRATIONS_ROUTE.BASE,
    loadComponent: () =>
      import('../features/integrations/integrations-list.component').then((mod) => mod.IntegrationsListComponent),
    data: {
      pageTitle: 'Integrations',
    },
    canActivate: [canActivateAuth, canActivateView('viewIntegrations'), canActivateOrganization],
  },
  {
    path: PHONE_NUMBERS_ROUTE.BASE,
    loadComponent: () =>
      import('../features/phone-numbers/phone-numbers-list.component').then((mod) => mod.PhoneNumbersListComponent),
    data: {
      pageTitle: 'Phone numbers',
    },
    canActivate: [canActivateAuth, canActivateView('viewPhoneNumbers'), canActivateOrganization],
  },
  {
    path: INTEGRATIONS_ROUTE.ADD,
    loadComponent: () =>
      import('../features/integrations/integrations.component').then((mod) => mod.IntegrationsComponent),
    data: {
      pageTitle: 'Create Integration',
    },
    canActivate: [canActivateAuth, canActivateView('viewIntegrationEditor'), canActivateOrganization],
  },
  {
    path: INTEGRATIONS_ROUTE.EDIT,
    loadComponent: () =>
      import('../features/integrations/integrations.component').then((mod) => mod.IntegrationsComponent),
    data: {
      pageTitle: 'Edit Integration',
    },
    canActivate: [canActivateAuth, canActivateView('viewIntegrationEditor'), canActivateOrganization],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: ({ url }) => redirectToOrganizationScopedPath(url),
  },
  {
    path: UNAUTHORIZED_PATH,
    loadComponent: () => import('../features/unauthorized/unauthorized.component').then((m) => m.UnauthorizedComponent),
  },
  {
    // Catches paths that lost their :organization segment (e.g. the org deleted from the URL).
    // Resolves the org-scoped target directly instead of bouncing through the empty route.
    path: '**',
    redirectTo: ({ url }) => redirectToOrganizationScopedPath(url),
  },
];
